import {put, select, takeLatest} from 'redux-saga/effects';

//Actions
import * as actions from '../../actions';

//Types
import {AxiosResponse} from 'axios';
import JSONFormData from '../../../utils/JSONFormData';
import {Account, DiscountPlan, CallControl} from '../../../services/endpoints';
import {api} from '../../services/axios';
import {APIErrorInterface, ReduxState} from '../../types';
import {ActionType} from 'typesafe-actions';
import { DiscountPlanList, GetAccountDashboardCallsPayload, GetAccountDashboardCallsResponse, PeersContactListItem, ProductInfo } from '../../actions/accountDashboard/payloads';
import { GetSipCallsListResponse, SipCall, SipCallState } from '../../actions/ringgroups/payloads';
import { calculateSipCallDuration } from '../wallboard/saga';
import dayjs from '../../../services/customDayJs';
import { getDurationFromSec } from '../../../utils/transformers';
import { RingGroupType } from '../../types/RingGroup';
import { editExtensionCallForwardingSettings } from '../extensions/callForwarding/saga';
import { compareObjectsAndReturnDifferencesInValues } from '../../../utils/compareObjects';
import { CounterInterface } from '../../types/CustomerAgreementCondition';
import { pagedAsyncDataRequest } from '../paged.data.saga';
import { ServiceFeature } from '../../types/ServiceFeature';
import { GET_SIP_CALLS_ACCESS_DENIED } from '../ringgroups/saga';
import {showToast} from '../../../utils/showToast';
import i18n from '../../../services/i18n';
import { CallHistory } from '../../types/CallHistory';

const dateFormat = 'YYYY-MM-DD HH:mm:ss';

export function* getAccountDashboardCalls(action: ActionType<typeof actions.getAccountDashboardCalls.request>) {
    const {session_id, csrf_token} = yield select(
        (state: ReduxState) => state.auth,
    );

    const body = new JSONFormData(session_id, csrf_token);
    body.setParams(action.payload);

    let activeCalls: GetSipCallsListResponse | null = null;

    try {
        if(action.payload.offset == 0) {
            const resSipCalls: AxiosResponse<GetSipCallsListResponse> = yield api.post(
                CallControl.GetSipCallsList,
                body,
            );

            if (resSipCalls?.data?.calls_list?.length) {
                for (const call of resSipCalls.data.calls_list) {
                    calculateSipCallDuration(call, resSipCalls.data.server_time);
                }
            }
            
            activeCalls = resSipCalls?.data;
        }
    } catch (error) {
        //@ts-ignore
        const apiError: APIErrorInterface | undefined = error?.response?.data;
        if(apiError?.faultcode == GET_SIP_CALLS_ACCESS_DENIED)
        {
            activeCalls = null;
        }
        else
        {
            yield put(actions.getAccountDashboardCalls.failure());
            return;
        }
    }
    
    let response: AxiosResponse<GetAccountDashboardCallsResponse>;

    try {
        response = yield api.post(Account.GetXdrList, body);
    } catch (error) {
        yield put(actions.getAccountDashboardCalls.failure());
        return;
    }
    
    yield put(actions.getAccountDashboardCalls.success({history: response.data, active: activeCalls}));
}

export function* updateSipCallStatusForAccountDashboardByWebSocketEvent(
    action: ActionType<typeof actions.updateSipCallStatusForAccountDashboardByWebSocketEvent.request>,
) {
    try {
        const call = action.payload;

        calculateSipCallDuration(call, undefined);
        const sipCallsList: SipCall[] = yield select((state: ReduxState) => state.accountDashboard.sipCallsList ?? []);
        let newList: SipCall[] = [...sipCallsList];

        if (call.previous_tracking_id) {
            const toRemoveCallIndex = newList.findIndex(e => e.tracking_id === call.tracking_id);
            if (toRemoveCallIndex !== -1) {
                newList = newList.filter((_, i) => i !== toRemoveCallIndex);
                call.tracking_id = call.previous_tracking_id;
                call.previous_tracking_id = undefined;
            }
        }

        const callIndex = newList.findIndex(e => e.tracking_id === call.tracking_id
            && e.call.id === call.call.id
            && e.call.tag === call.call.tag);

        if (call.state === SipCallState.Terminated) {
            if (callIndex !== -1) {
                newList = newList.filter((_, i) => i !== callIndex);
            }
        } else if (callIndex === -1) {
            newList = [call, ...newList];
        } else {
            newList = newList.map((e, i) => (i === callIndex ? call : e));
        }

        yield put(actions.updateSipCallStatusForAccountDashboardByWebSocketEvent.success(newList));

        if (call.state === SipCallState.Terminated) {
            const lastCallsPayload: GetAccountDashboardCallsPayload | undefined =
                yield select((state: ReduxState) => state.accountDashboard.lastCallsPayload);
            if (lastCallsPayload) {
                yield put(
                    actions.getAccountDashboardCalls.request({
                        ...lastCallsPayload,
                        offset: 0,
                        to_date: dayjs.utc().format(dateFormat),
                    }),
                );
            }
        }
    } catch (error) {
        //@ts-ignore
        const apiError = error?.response?.data;
        yield put(
            actions.updateSipCallStatusForAccountDashboardByWebSocketEvent.failure(apiError),
        );
    }
}

export function* recalcSipCallsDurationForAcoountDashboard() {
    try {
        const sipCallsList: SipCall[] = yield select((state: ReduxState) => state.accountDashboard.sipCallsList ?? []);
        for (const call of sipCallsList) {
            if (call.duration === undefined || call.duration === null) {
                call.duration = "00:00:00";
                call.durationFormatSec = undefined;
                continue;
            }
            const baseDate = '2024-01-01';
            const baseDateFull = '2024-01-01 00:00:00';
            const updatedDuration = dayjs(baseDate + 'T' + call.duration, dateFormat).add(1, 'seconds');
            const formatTime = dateFormat.split(' ')[1];
            call.duration = updatedDuration.format(formatTime);
            const djs = dayjs(baseDateFull, dateFormat);
            const diff2 = updatedDuration.diff(djs) / 1000;
            call.durationFormatSec = getDurationFromSec(diff2).string;
        }
        yield put(actions.recalcSipCallsDurationForAcoountDashboard.success([...sipCallsList]));
    } catch (error) {
        //@ts-ignore
        const apiError = error?.response?.data;
        yield put(
            actions.recalcSipCallsDurationForAcoountDashboard.failure(apiError),
        );
    }
}

export function* getAccountDashboardRingGroupList() {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);

        body.setParams({});

        const response: AxiosResponse<{
            huntgroup_list: RingGroupType[]
        }> = yield api.post(Account.GetAccountHuntGroupsList, body);

        yield put(actions.getAccountDashboardRingGroupList.success(response.data?.huntgroup_list ?? []));
    } catch (error) {
        //@ts-ignore
        const apiError = error?.response?.data;
        yield put(
            actions.getAccountDashboardRingGroupList.failure(apiError),
        );
    }
}

export function* updateHuntgroupsSubscription(
    action: ActionType<typeof actions.updateHuntgroupsSubscription.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);

        body.setParams(action.payload);

        yield api.post(Account.UpdateHuntgroupsSubscription, body);

        action.payload.callback?.();

        showToast(i18n.t<string>('screens:accountDashboard.settingsHaveBeenUpdated'));
        
        yield put(actions.updateHuntgroupsSubscription.success());
    } catch (error) {
        //@ts-ignore
        const apiError = error?.response?.data;
        yield put(
            actions.updateHuntgroupsSubscription.failure(apiError),
        );
    }
}

export function* updateAccountDashboardCallForwardingSettings(
    action: ActionType<typeof actions.updateAccountDashboardCallForwardingSettings.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);

        body.setParams(action.payload);

        const dataToSave = compareObjectsAndReturnDifferencesInValues(
            action.payload.initialValues,
            action.payload.changedValues,
        );
        
        yield editExtensionCallForwardingSettings(dataToSave, action);
        
        action.payload.callback?.();

        showToast(i18n.t<string>('screens:accountDashboard.settingsHaveBeenUpdated'));
        
        yield put(actions.updateAccountDashboardCallForwardingSettings.success());
    } catch (error) {
        //@ts-ignore
        const apiError = error?.response?.data;
        yield put(
            actions.updateAccountDashboardCallForwardingSettings.failure(apiError),
        );
    }
}

export function* getAccountProductInfo(
    action: ActionType<typeof actions.getAccountProductInfo.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);

        body.setParams(action.payload);

        const response: AxiosResponse<{
            product_info: ProductInfo
        }> = yield api.post(Account.GetAccountProductInfo, body);
        
        yield put(actions.getAccountProductInfo.success(response.data.product_info));
    } catch (error) {
        //@ts-ignore
        const apiError = error?.response?.data;
        yield put(
            actions.getAccountProductInfo.failure(apiError),
        );
    }
}

export function* getAccountDashboardCounters(
    action: ActionType<typeof actions.getAccountDashboardCounters.request>,) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);

        body.setParams(action.payload);

        const res: AxiosResponse<{
            counter_list: CounterInterface[];
        }> = yield api.post(Account.GetFullVdCounterInfo, body);

        yield put(actions.getAccountDashboardCounters.success(res.data.counter_list))

    } catch (error) {
        //@ts-ignore
        const apiError = error?.response?.data;
        yield put(
            actions.getAccountDashboardCounters.failure(apiError),
        );
    }
}

export function* getAccountDashboardDiscountPlanList(
    action: ActionType<typeof actions.getAccountDashboardDiscountPlanList.request>,) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);

        body.setParams(action.payload);

        const res: AxiosResponse<{
            discount_plan_list: DiscountPlanList[];
        }> = yield api.post(DiscountPlan.GetDiscountPlanList, body);

        yield put(actions.getAccountDashboardDiscountPlanList.success(res.data.discount_plan_list))

    } catch (error) {
        //@ts-ignore
        const apiError = error?.response?.data;
        yield put(
            actions.getAccountDashboardDiscountPlanList.failure(apiError),
        );
    }
}

export function* getAccountDashboardPeersContactList() {
    try {
        const response: { response: {
                peers_contacts_list: PeersContactListItem[]
            }} = yield pagedAsyncDataRequest<{
                peers_contacts_list: PeersContactListItem[]
            }>(
            Account.GetPeersContactList,
            {
                only_with_extensions: 1
            },
        );

        const contacts: PeersContactListItem[] =
            response.response.peers_contacts_list;

        // @ts-ignore
        yield put(
            actions.getAccountDashboardPeersContactList.success(contacts),
        );
    } catch (error) {
        //@ts-ignore
        const apiError = error?.response?.data;
        yield put(actions.getAccountDashboardPeersContactList.failure(apiError));
    }
}

export function* getAccountDashboardServiceFeatures(
    action: ActionType<typeof actions.getAccountDashboardServiceFeatures.request>,
) {
    try {
        const { session_id, csrf_token } = yield select((state) => state.auth);

        const body = new JSONFormData(session_id, csrf_token);
        body.setParams(action.payload);

        const res: AxiosResponse<{
            service_features: ServiceFeature[];
        }> = yield api.post(Account.GetServiceFeatures, body);

        yield put(
            actions.getAccountDashboardServiceFeatures.success(res.data.service_features),
        );
    } catch (error) {
        //@ts-ignore
        const apiError = error?.response?.data;
        yield put(
            actions.getAccountDashboardServiceFeatures.failure(apiError),
        );
    }
}

export function* clearCallRecordingIds(
    action: ActionType<typeof actions.clearCallRecordingIds.request>,
) {
    const xdr_list: CallHistory[] | undefined = yield select((state) => state.accountDashboard?.xdr_list);

    const updItems = (xdr_list ?? []).map(upd => {
        const match = action.payload.find(e => e.call_id === upd.call_id);
        return match ? { ...upd, cr_download_ids: undefined } : upd;
    });

    yield put(
        actions.clearCallRecordingIds.success(updItems),
    );
}

export const accountDashboardSaga = [
    takeLatest(actions.getAccountDashboardCalls.request, getAccountDashboardCalls),
    takeLatest(actions.updateSipCallStatusForAccountDashboardByWebSocketEvent.request, updateSipCallStatusForAccountDashboardByWebSocketEvent),
    takeLatest(actions.recalcSipCallsDurationForAcoountDashboard.request, recalcSipCallsDurationForAcoountDashboard),
    takeLatest(actions.getAccountDashboardRingGroupList.request, getAccountDashboardRingGroupList),
    takeLatest(actions.updateHuntgroupsSubscription.request, updateHuntgroupsSubscription),
    takeLatest(actions.updateAccountDashboardCallForwardingSettings.request, updateAccountDashboardCallForwardingSettings),
    takeLatest(actions.getAccountProductInfo.request, getAccountProductInfo),
    takeLatest(actions.getAccountDashboardCounters.request, getAccountDashboardCounters),
    takeLatest(actions.getAccountDashboardDiscountPlanList.request, getAccountDashboardDiscountPlanList),
    takeLatest(actions.getAccountDashboardPeersContactList.request, getAccountDashboardPeersContactList),
    takeLatest(actions.getAccountDashboardServiceFeatures.request, getAccountDashboardServiceFeatures),
    takeLatest(actions.clearCallRecordingIds.request, clearCallRecordingIds),
];
